import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ReportShell } from "@/features/reports/ReportShell";
import { useBranch } from "@/context/BranchContext";
import type { ReportFilters } from "@/features/reports/types";
import { Loader2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/integrations/api/client";
import { format } from "date-fns";

export const Route = createFileRoute("/_authenticated/reports/procurement-cycle")({
  component: ProcurementCycleReport,
});

function ProcurementCycleReport() {
  const [filters, setFilters] = useState<ReportFilters>({});
  const { currentBranchId } = useBranch();
  
  const { data, isLoading } = useQuery({
    queryKey: ["reports", "procurement-cycle", currentBranchId],
    queryFn: async () => {
      const p = new URLSearchParams();
      if (currentBranchId) p.set("branch", currentBranchId);
      const res = await apiGet(`/reports/procurement-cycle/?${p.toString()}`);
      return res as any[];
    }
  });

  const formatDate = (d: string | null) => d ? format(new Date(d), "PP") : "—";

  const exportRows = useMemo(() => () => (data ?? []).map((r) => ({
    "Indent No": r.indent_number,
    "Type": r.indent_type,
    "Department": r.department,
    "Status": r.status,
    "Submitted": formatDate(r.submitted_at),
    "Approved": formatDate(r.purchase_approved_at),
    "PO Raised": formatDate(r.po_raised_at),
    "GRN Received": formatDate(r.grn_received_at),
  })), [data]);

  return (
    <ReportShell
      id="R-22"
      title="Indent Status & Procurement Cycle Time"
      description="Indent pipeline, approvals and procurement cycle metrics."
      filters={filters}
      onFiltersChange={setFilters}
      onExport={exportRows}
      exportFilename="Procurement_Cycle"
      allowedFilters={["branchId"]}
    >
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Indent No</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead>Approved</TableHead>
                  <TableHead>PO Raised</TableHead>
                  <TableHead>GRN Received</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.length === 0 && (
                  <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground">No data found</TableCell></TableRow>
                )}
                {data?.map((r: any) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">{r.indent_number}</TableCell>
                    <TableCell>{r.indent_type}</TableCell>
                    <TableCell>{r.department}</TableCell>
                    <TableCell>{formatDate(r.submitted_at)}</TableCell>
                    <TableCell>{formatDate(r.purchase_approved_at)}</TableCell>
                    <TableCell>{formatDate(r.po_raised_at)}</TableCell>
                    <TableCell>{formatDate(r.grn_received_at)}</TableCell>
                    <TableCell><Badge variant="outline">{r.status}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </ReportShell>
  );
}
